import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import Calculator from './modules/calculator/calculator';
import reportWebVitals from './reportWebVitals';
import {ThemeProvider} from "@mui/material";
import {theme} from "./styles/theme";
import {initReactI18next} from "react-i18next";
import i18n from "i18next";
import CalculatorC2C from "./modules/calculator/calculatorC2C";
import {types} from "sass";
import {LocationsWrapper} from "./modules/locations/locationsWrapper";
import {WidgetType} from "./modules/common/enums/widgetType";
import {CalculatorVariant} from "./modules/common/enums/calculatorVariant";
import Error = types.Error;

const rootElement = document.getElementById('react-widgets') as HTMLElement
const root = ReactDOM.createRoot(rootElement)

const init = (lang: string) => {
    i18n
        .use(initReactI18next) // passes i18n down to react-i18next
        .init({
            // the translations
            // (tip move them in a JSON file and import them,
            // or even better, manage them via a UI: https://react.i18next.com/guides/multiple-translation-files#manage-your-translations-with-a-management-gui)
            resources: {
                en: {
                    translation: {
                        calculate: "Calculate",
                        optional: "optional",
                        country: {
                            "placeholder": "Select a country for delivery"
                        },
                        zip: {
                            placeholder: "ZIP Code"
                        },
                        postalCode: {
                            placeholder: "Postal Code"
                        },
                        weight: {
                            placeholder: "Item weight ({{weightLimit}} {{weightLimitUnit}} max)",
                            kg: 'kg',
                            lb: 'lb'
                        },
                        dimension: {
                            in: 'in',
                            cm: 'cm'
                        },
                        deliveryMethods: {
                            sort: {
                                min: "Cheaper first",
                                max: "Expensive first"
                            },
                            title: "Your delivery options",
                            nothingFound: {
                                title: "No delivery options found",
                                description1: "Unfortunately, delivery to the selected destination country is currently unavailable.",
                                description2: "Please select another destination country from the list above."
                            },
                            days: 'days',
                            from: 'from',
                            upto: 'up to',
                            for: 'for',
                            footnote: "Volumetric weight may apply.",
                        },
                        length: {
                            placeholder: "Length",
                        },
                        width: {
                            placeholder: "Width",
                        },
                        height: {
                            placeholder: "Height",
                        },
                    }
                },
                ru: {
                    translation: {
                        calculate: "Рассчитать",
                        optional: "необязательно",
                        country: {
                            "placeholder": "Выберите страну доставки"
                        },
                        zip: {
                            placeholder: "Почтовый индекс"
                        },
                        weight: {
                            placeholder: "Вес товара (до {{weightLimit}} {{weightLimitUnit}})",
                            kg: 'кг',
                            lb: 'фт'
                        },
                        deliveryMethods: {
                            sort: {
                                min: "Сначала дешевле",
                                max: "Сначала дороже"
                            },
                            title: "Ваши варианты доставки",
                            nothingFound: {
                                title: "Вариантов доставки не найдено",
                                description1: "К сожалению, доставка в выбранную страну назначения сейчас не осуществляется.",
                                description2: "Пожалуйста, выберите в списке выше другую страну назначения."
                            },
                            days: 'дней',
                            from: 'от',
                            upto: 'до',
                            for: 'за',
                            footnote: "Может применяться объёмный вес.",
                        }
                    }
                },
                ua: {
                    translation: {
                        calculate: "Розрахувати",
                        optional: "необов'язково",
                        country: {
                            placeholder: "Виберіть країну доставки"
                        },
                        zip: {
                            placeholder: "Поштовий індекс"
                        },
                        weight: {
                            placeholder: "Вага товару (до {{weightLimit}} {{weightLimitUnit}})",
                            kg: 'кг',
                            lb: 'фт'
                        },
                        deliveryMethods: {
                            sort: {
                                min: "Спочатку дешевше",
                                max: "Спочатку дорожче"
                            },
                            title: "Ваші варіанти доставки",
                            nothingFound: {
                                title: "Варіантів доставки не знайдено",
                                description1: "На жаль, доставка до обраної країни призначення наразі не здійснюється.",
                                description2: "Будь ласка, виберіть у списку вище іншу країну призначення."
                            },
                            days: 'днів',
                            from: 'від',
                            upto: 'до',
                            for: 'за',
                            footnote: "Може застосовуватися об'ємна вага.",
                        }
                    }
                }
            },
            debug: true,
            lng: lang,
            fallbackLng: "en",

            interpolation: {
                escapeValue: false // react already safes from xss => https://www.i18next.com/translation-function/interpolation#unescape
            }
        })
        .then(() => {
        })
        .catch((error) => {
            console.log(error)
        });
};

init(rootElement?.dataset?.lang ?? 'en')


const widgetType: WidgetType = rootElement?.dataset?.widget as WidgetType

const calculatorVariant: CalculatorVariant = rootElement?.dataset?.variant as CalculatorVariant ?? CalculatorVariant.Default

const renderWidget = (widgetType: WidgetType, calculatorVariant: CalculatorVariant) => {
    switch (widgetType) {
        case WidgetType.Calculator: {
            switch (calculatorVariant) {
                case CalculatorVariant.Default:
                    return <Calculator/>
                case CalculatorVariant.C2C:
                    return <CalculatorC2C/>
                default:
                    new Error("unknown calculator variant")
                    break;
            }
            break;
        }
        case WidgetType.Locations:
            return <LocationsWrapper/>
        default:
            new Error("unknown widget type")
            break;
    }
}

root.render(
    <ThemeProvider theme={theme}>
        {renderWidget(widgetType, calculatorVariant)}
    </ThemeProvider>
);

reportWebVitals();
